import { useMutation, useQuery, useQueryClient } from "react-query";
import type {
  ErstelleTestSzenarioVersion,
  SetzeTestSzenario,
  TestPlan,
  TestSzenarioEntwurf,
  TestTyp,
  TestTypSucheEintrag,
} from "../../api";
import { createId, useGetApi } from "../../data";
import { ServoTypFilterType } from "../servo-typen/ServoTypFilter";

export function useTestSzenarios(
  opts: { sortierung?: string; seite?: number; max?: number } = {}
) {
  const getApi = useGetApi();

  return useQuery(["test-szenarios", opts], async () =>
    (await getApi()).testSzenarios.sucheTestSzenarios(
      opts.sortierung,
      opts.seite,
      opts.max
    )
  ).data!;
}

export function useTestSzenario(id: string) {
  const getApi = useGetApi();

  return useQuery(
    ["test-szenarios", id],
    async () => {
      return (await getApi()).testSzenarios.getTestSzenario(id);
    },
    { useErrorBoundary: false }
  ).data!;
}

export function useSetzeTestSzenario() {
  const getApi = useGetApi();
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, daten }: { id?: string; daten: SetzeTestSzenario }) =>
      (await getApi()).testSzenarios.setzeTestSzenario(id ?? createId(), daten),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries("test-szenarios");
      },
    }
  );
}

export function useGetServoTypenForTestSzenario({
  id,
  seite,
  max,
}: {
  id: string;
  seite?: number;
  max?: number;
}) {
  const getApi = useGetApi();

  return useQuery(
    ["test-szenario-servo-typen-assigned", id, { seite, max }],
    async () =>
      (await getApi()).testSzenarios.getTestSzenarioServoTypen(id, seite, max)
  ).data!;
}

export function useTestSzenarioVersionen(opts: {
  suche?: string;
  servoTypId?: string;
  testSzenarioId?: string;
  aktuell?: boolean;
  sortierung?: string;
  seite?: number;
  max?: number;
}) {
  const getApi = useGetApi();

  return useQuery(["test-szenarios-versionen", opts], async () =>
    (await getApi()).testSzenarios.sucheTestSzenarioVersionen(
      opts.suche,
      opts.servoTypId,
      opts.testSzenarioId,
      opts.aktuell,
      opts.sortierung,
      opts.seite,
      opts.max
    )
  ).data!;
}

export function useLadeSzenarioVersionen(opts: {
  testSzenarioId: string;
  sortierung?: string;
  seite?: number;
  max?: number;
}) {
  const getApi = useGetApi();

  return useQuery(["test-szenarios-versionen", opts], async () =>
    (await getApi()).testSzenarios.ladeTestSzenarioVersionen(
      opts.testSzenarioId,
      opts.sortierung,
      opts.seite,
      opts.max
    )
  ).data!;
}

export function useTestSzenarioVersion(versionId: string) {
  const getApi = useGetApi();
  return useQuery(
    ["test-szenario-versionen", versionId],
    async () => {
      return (await getApi()).testSzenarios.getTestSzenarioVersion(versionId);
    },
    { useErrorBoundary: false }
  ).data!;
}

export function useErstelleTestSzenarioVersion(testSzenarioId: string) {
  const getApi = useGetApi();
  const queryClient = useQueryClient();

  return useMutation(
    async (daten: ErstelleTestSzenarioVersion) =>
      (await getApi()).testSzenarios.erstelleTestSzenarioVersion(
        testSzenarioId,
        daten
      ),
    {
      onSuccess: async () =>
        queryClient.invalidateQueries(["test-szenarios", testSzenarioId]),
    }
  );
}

export function useTestSzenarioEntwurf(testSzenarioId: string) {
  const getApi = useGetApi();

  return useQuery(["test-szenarios", testSzenarioId, "entwurf"], async () =>
    (await getApi()).testSzenarios.getTestSzenarioEntwurf(testSzenarioId)
  ).data!;
}

export function useSpeichereTestSzenarioEntwurf(testSzenarioId: string) {
  const getApi = useGetApi();
  const queryClient = useQueryClient();
  const queryKey = ["test-szenarios", testSzenarioId, "entwurf"];

  return useMutation(
    async (plan: TestPlan) =>
      (await getApi()).testSzenarios.speichereTestSzenarioEntwurf(
        testSzenarioId,
        { plan }
      ),
    {
      onMutate: (plan) => {
        const current = queryClient.getQueryData<TestSzenarioEntwurf>(queryKey);
        console.debug("Storing current state:", current);
        console.debug("Optimistically saving state:", { ...current, plan });
        queryClient.setQueryData(queryKey, { ...current, plan });
        return { current };
      },
      onSuccess: (data) => {
        console.debug("Updating cached value after success:", data);
        queryClient.setQueryData(queryKey, data);
      },
      onError: (_, __, context) => {
        if (context?.current) {
          console.debug("Restoring state after error:", context.current);
          queryClient.setQueryData(queryKey, context.current);
        }
      },
    }
  );
}

export function useAlleTestTypen(): Array<TestTypSucheEintrag> {
  const getApi = useGetApi();

  return useQuery("test-typen", async () =>
    (await getApi()).testTypen.sucheTestTypen(0, 1000, [
      "parameter",
      "messungen",
    ])
  ).data!.elemente;
}

export function useTestTyp(id: string): TestTyp {
  const testTyp = useAlleTestTypen().find((t) => t.id === id);
  if (!testTyp) throw new Error("Test-Typ nicht gefunden: " + id);
  return testTyp as unknown as TestTyp;
}

export function useAddServoTypToTestSzenario() {
  const getApi = useGetApi();
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      testSzenarioId,
      servoTypId,
    }: {
      testSzenarioId: string;
      servoTypId: string;
    }) =>
      (await getApi()).testSzenarios.addServoTypToTestSzenario(testSzenarioId, {
        servoTypId,
      }),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(["test-szenario-servo-typen-all"]);
        await queryClient.invalidateQueries([
          "test-szenario-servo-typen-assigned",
        ]);
      },
    }
  );
}

export function useRemoveServoTypFromTestSzenario() {
  const getApi = useGetApi();
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      testSzenarioId,
      servoTypId,
    }: {
      testSzenarioId: string;
      servoTypId: string;
    }) =>
      (await getApi()).testSzenarios.deleteServoTypForTestSzenario(
        testSzenarioId,
        {
          servoTypId,
        }
      ),
    {
      onSuccess: async () => {
        await queryClient.invalidateQueries(["test-szenario-servo-typen-all"]);
        await queryClient.invalidateQueries([
          "test-szenario-servo-typen-assigned",
        ]);
      },
    }
  );
}

export function useUnassignedServoTypen({
  id,
  seite,
  max,
  filter,
}: {
  id: string;
  seite?: number;
  max?: number;
  filter?: ServoTypFilterType;
}) {
  const getApi = useGetApi();

  return useQuery(
    ["test-szenario-servo-typen-all", id, { seite, max, filter }],
    async () =>
      (await getApi()).testSzenarios.getUnassignedServoTypenForTestSzenario(
        id,
        seite,
        max,
        filter?.suche
      )
  ).data!;
}
